<template>
  <Teleport to="body">
    <cl-modal
      :close-modal-text="$t('close')"
      :visible="true"
      :test-id="dataTestId"
      @on-close="onClose"
    >
      <template #[`header.title`]>
        <cl-heading :size="'h4'">{{ confirmationModalHeading }}</cl-heading>
      </template>
      <div>
        <div v-dompurify-html="intro" />
        <ul class="tw-list-disc tw-ml-4 tw-my-4">
          <li>
            {{ $t("notify-admins") }}
          </li>
          <li v-if="isMspRoute">
            {{ $t("setting-changed") }}
          </li>
        </ul>
      </div>

      <template #[`footer.decline`]>
        <slot name="decline">
          <cl-button
            variant="secondary-ghost"
            :data-test-id="`${dataTestId}-cancel`"
            @on-click="onClose"
          >
            {{ $t("cancel") }}
          </cl-button>
        </slot>
      </template>
      <template #[`footer.accept`]>
        <slot name="confirm">
          <cl-button
            variant="secondary"
            :data-test-id="`${dataTestId}-confirm`"
            :disabled="loading"
            @on-click="onConfirm"
          >
            <cl-spinner
              :loading-text="$t('loading')"
              v-if="loading"
              :size="'small'"
              class="tw-mr-2"
            />
            {{ $t("confirm") }}
          </cl-button>
        </slot>
      </template>
    </cl-modal>
  </Teleport>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useIsMspRoute } from "@/composables/useIsMspRoute";

type Props = {
  visible?: boolean;
  loading?: boolean;
  dataTestId: string;
  isAuto: boolean;
  isToJunk: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  visible: false,
  loading: false,
});

const emit = defineEmits<{
  (event: "on-confirm"): void;
  (event: "on-close"): void;
}>();

const onClose = () => {
  emit("on-close");
};
const onConfirm = () => {
  emit("on-confirm");
};

const { t } = useI18n();

const { isMspRoute } = useIsMspRoute();

const confirmationModalHeading = computed(() =>
  props.isAuto ? t("enable-auto-remediation") : t("enable-manual-remediation"),
);

const folder = computed(() =>
  t(props.isToJunk ? "junk-email-folder" : "deleted-items-folder"),
);

const intro = computed(() => {
  if (props.isAuto) {
    return isMspRoute.value
      ? t("enable-auto-remediation-with-folder", { folder: folder.value })
      : t("enable-auto-remediation-customer", { folder: folder.value });
  }

  return isMspRoute.value
    ? t("enable-manual-remediation-intro")
    : t("enable-manual-remediation-customer");
});
</script>
